import { useEffect, useRef } from 'react';
import SignatureCanvas from 'react-signature-canvas';

type UseSignaturePadsOptions = {
  /** Called with the full-pad PNG data URL when a signature stroke ends. */
  onCapture: (id: string, dataUrl: string) => void;
  /** Optional error sink (e.g. Sentry) if the canvas export throws. */
  onCaptureError?: (err: unknown) => void;
};

/**
 * Manages react-signature-canvas pad refs for a positioned custom form.
 *
 * Captures the full-pad PNG on stroke-end — guarded by an isMounted ref so a touch
 * that lifts during a page transition can't setState on an unmounted tree — and
 * drops lingering pointer listeners on unmount (tablet/kiosk touch-device
 * hardening). Shared by the staff review screen and the patient portal so the
 * capture behavior can't drift between the two.
 */
export const useSignaturePads = ({ onCapture, onCaptureError }: UseSignaturePadsOptions) => {
  const sigRefs = useRef<Record<string, SignatureCanvas | null>>({});
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    const pads = sigRefs.current;
    return () => {
      isMounted.current = false;
      Object.values(pads).forEach(pad => {
        try {
          pad?.off();
        } catch {
          /* ignore */
        }
      });
    };
  }, []);

  const capture = (id: string) => {
    const pad = sigRefs.current[id];
    if (!pad || pad.isEmpty()) return;
    try {
      // Capture the FULL pad, not getTrimmedCanvas(): the signature is drawn,
      // re-shown, and PDF-rendered in the same fixed box, so trimming to the ink
      // bounds and then stretching that crop to fill the box blows a small mark up
      // to fill it ("zoom"). The full pad preserves the mark's real size/position.
      const dataUrl = pad.getCanvas().toDataURL('image/png');
      if (isMounted.current) onCapture(id, dataUrl);
    } catch (err) {
      onCaptureError?.(err);
    }
  };

  const clear = (id: string) => {
    sigRefs.current[id]?.clear();
  };

  return { sigRefs, capture, clear };
};

export default useSignaturePads;
